// Read-only operational history for a single class.
//
// Cleanest implementation of the "History" requirement: nothing is duplicated
// or archived into a new table. History is simply the SAME canonical rows
// (instructor_class_assignments, class_payments) filtered to windows that have
// already closed. The main editor tabs stay focused on the current/upcoming
// session; everything settled lives here.
//
// This panel NEVER writes. It exists so past instructors, past sessions and
// past enrollment can be inspected without risking live data.

import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";

interface Props {
  classId?: string | null;
  slug: string;
}

interface PastAssignment {
  id: string;
  instructor_id: string;
  status: string;
  session_label: string | null;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
}

interface PastSession {
  label: string;
  seats: number;
}

/**
 * A settled registration, read straight from `class_payments`. Nothing is
 * copied anywhere — this is the authoritative row, shown read-only to an
 * authenticated admin and never exposed publicly.
 */
interface PastRegistration {
  id: string;
  label: string;
  adult_name: string | null;
  email: string | null;
  child_name: string | null;
  child_age: number | null;
  created_at: string | null;
  payment_status: string | null;
  amount_paid: number | null;
  refunded_amount_cents: number | null;
  balance_due_cents: number | null;
  transfer_credit_cents: number | null;
  is_complimentary: boolean | null;
  removed_reason: string | null;
}

const money = (cents: number | null | undefined) =>
  typeof cents === "number" ? `$${(cents / 100).toFixed(2)}` : "—";

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "unknown date");

const TERMINAL = ["completed", "replaced", "cancelled", "historical", "on_hold"];
const today = () => new Date().toISOString().slice(0, 10);

export function ClassHistoryPanel({ classId, slug }: Props) {
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<PastAssignment[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [sessions, setSessions] = useState<PastSession[]>([]);
  const [registrations, setRegistrations] = useState<PastRegistration[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const aq = supabase
        .from("instructor_class_assignments")
        .select("id,instructor_id,status,session_label,start_date,end_date,notes,class_id,class_slug")
        .order("start_date", { ascending: false });
      const [aRes, pRes] = await Promise.all([
        classId ? aq.or(`class_id.eq.${classId},class_slug.eq.${slug}`) : aq.eq("class_slug", slug),
        supabase
          .from("class_payments")
          .select(
            "id,class_id,canonical_session_label,selected_sessions,adult_name,email,child_name,child_age,created_at,payment_status,amount_paid,refunded_amount_cents,balance_due_cents,transfer_credit_cents,is_complimentary,removed_reason,is_test_record,hidden",
          )
          .or(classId ? `class_id.eq.${classId},class_id.eq.${slug}` : `class_id.eq.${slug}`)
          .order("created_at", { ascending: false }),
      ]);
      if (!alive) return;

      const past = ((aRes.data as PastAssignment[]) ?? []).filter(
        (r) =>
          TERMINAL.includes((r.status ?? "").toLowerCase()) ||
          (!!r.end_date && r.end_date < today()),
      );
      setAssignments(past);

      const ids = [...new Set(past.map((r) => r.instructor_id).filter(Boolean))];
      if (ids.length) {
        const { data } = await supabase
          .from("instructors")
          .select("id,full_name,preferred_name")
          .in("id", ids);
        if (!alive) return;
        setNames(
          Object.fromEntries(
            (data ?? []).map((i: any) => [i.id, i.preferred_name?.trim() || i.full_name]),
          ),
        );
      }

      const rows = ((pRes.data as any[]) ?? []).filter(
        (p) => !p.is_test_record && !p.hidden,
      );
      const labelOf = (p: any) =>
        p.canonical_session_label ||
        (Array.isArray(p.selected_sessions) ? p.selected_sessions.join(", ") : "") ||
        "Unlabeled session";
      const bySession = new Map<string, number>();
      for (const p of rows) {
        const label = labelOf(p);
        bySession.set(label, (bySession.get(label) ?? 0) + 1);
      }
      setSessions([...bySession].map(([label, seats]) => ({ label, seats })));
      setRegistrations(rows.map((p) => ({ ...p, label: labelOf(p) })) as PastRegistration[]);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [classId, slug]);

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading history…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <History className="h-4 w-4 mt-0.5 shrink-0" />
        <p>
          Read-only. Nothing here can be edited or deleted — past instructor windows and past
          sessions are preserved exactly as they happened, including for payroll.
        </p>
      </div>

      <section>
        <h4 className="text-sm font-semibold mb-2">Past instructor assignments</h4>
        {assignments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No closed assignments yet.</p>
        ) : (
          <ul className="space-y-2">
            {assignments.map((a) => (
              <li
                key={a.id}
                className="rounded-md border border-border p-2.5 text-sm flex flex-wrap items-center gap-2"
              >
                <span className="font-medium">{names[a.instructor_id] ?? "Instructor"}</span>
                <Badge variant="outline" className="text-[10px]">
                  {a.status}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {a.session_label ?? "No session label"} ·{" "}
                  {a.start_date ?? "no start"} → {a.end_date ?? "no end"}
                </span>
                {a.notes ? (
                  <span className="w-full text-[11px] text-muted-foreground">{a.notes}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="text-sm font-semibold mb-2">Historical registrations</h4>
        <p className="text-xs text-muted-foreground mb-2">
          Settled seats from past sessions. These never count toward current enrollment,
          capacity, remaining seats or the live roster.
        </p>
        {registrations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No registrations recorded for this class.</p>
        ) : (
          <ul className="space-y-2">
            {registrations.map((r) => (
              <li key={r.id} className="rounded-md border border-border p-2.5 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{r.child_name ?? "Child not recorded"}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {r.label}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {r.payment_status ?? "unknown"}
                  </Badge>
                  {r.is_complimentary ? (
                    <Badge variant="outline" className="text-[10px]">complimentary</Badge>
                  ) : null}
                  {r.removed_reason ? (
                    <Badge variant="outline" className="text-[10px]">removed</Badge>
                  ) : null}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {r.adult_name ?? "Parent not recorded"}
                  {r.email ? ` · ${r.email}` : ""}
                  {r.child_age ? ` · age ${r.child_age}` : ""}
                  {` · registered ${when(r.created_at)}`}
                </div>
                <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                  Paid {money(r.amount_paid)}
                  {r.transfer_credit_cents ? ` · credit ${money(r.transfer_credit_cents)}` : ""}
                  {r.balance_due_cents ? ` · balance due ${money(r.balance_due_cents)}` : ""}
                  {r.refunded_amount_cents ? ` · refunded ${money(r.refunded_amount_cents)}` : ""}
                </div>
                {r.removed_reason ? (
                  <div className="mt-1 text-[11px] text-muted-foreground">{r.removed_reason}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="text-sm font-semibold mb-2">Enrollment by session</h4>
        {sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No registrations recorded for this class.</p>
        ) : (
          <ul className="space-y-1.5">
            {sessions.map((s) => (
              <li
                key={s.label}
                className="flex items-center justify-between rounded-md border border-border px-2.5 py-2 text-sm"
              >
                <span>{s.label}</span>
                <span className="tabular-nums text-muted-foreground">{s.seats} registered</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default ClassHistoryPanel;
